import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { AlertTriangle, CheckSquare, Shield, ArrowRight, HelpCircle, ArrowLeft, FileText, AlertOctagon, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { scamProtocols } from '../data/scamProtocols';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { PageLayout } from '../components/PageLayout';
import { SEO } from '../components/SEO';
export function ProtocolPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    type
  } = useParams();
  const protocol = scamProtocols.find(p => p.id === type);
  if (!protocol) {
    return <Navigate to={`/${language}`} replace data-id="element-1003" />;
  }
  const title = protocol.title[language] || protocol.title['en'];
  const description = protocol.description[language] || protocol.description['en'];
  const steps = protocol.steps[language] || protocol.steps['en'];
  const warningSigns = protocol.warningSigns[language] || protocol.warningSigns['en'];
  const evidenceChecklist = protocol.evidenceChecklist[language] || protocol.evidenceChecklist['en'];
  return <>
      <SEO title={title} description={description} data-id="element-1004" />

      <PageLayout title={title} subtitle={description} icon={<Shield className="h-8 w-8 text-primary-600" data-id="element-1006" />} maxWidth="xl" background="slate" data-id="element-1005">
        {/* Breadcrumb */}
        <div className="mb-8" data-id="element-1007">
          <Link to={`/${language}`} className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-primary-600 transition-colors" data-id="element-1008">
            <ArrowLeft className="w-4 h-4 mr-1" data-id="element-1009" />
            {t('protocol.backToHome')}
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-1010">
          {/* Sidebar - Mobile: First, Desktop: Last (Sticky) */}
          <div className="lg:col-span-1 lg:col-start-3 lg:row-start-1 space-y-6 lg:sticky lg:top-24 order-1 lg:order-2" data-id="element-1011">
            {/* Assessment CTA - High Priority */}
            <Card className="p-6 bg-indigo-50 border-l-4 border-indigo-500 shadow-sm hover:shadow-md transition-shadow" data-id="element-1012">
              <div className="flex items-start gap-3 mb-4" data-id="element-1013">
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600" data-id="element-1014">
                  <HelpCircle className="h-5 w-5" data-id="element-1015" />
                </div>
                <div data-id="element-1016">
                  <h3 className="font-bold text-indigo-900" data-id="element-1017">
                    {t('protocol.unsureIfScam')}
                  </h3>
                  <p className="text-sm text-indigo-700 mt-1" data-id="element-1018">
                    {t('protocol.assessmentDesc')}
                  </p>
                </div>
              </div>
              <div className="space-y-2" data-id="element-1019">
                <Button href={`/${language}/chat-assessment/${type}`} variant="accent" size="sm" fullWidth className="bg-indigo-600 hover:bg-indigo-700 text-white border-transparent" data-id="element-1020">
                  💬 {t('protocol.chatAssessment')}
                </Button>
                <Button href={`/${language}/check-scam/${type}`} variant="outline" size="sm" fullWidth data-id="element-1021">
                  📋 {t('protocol.quickChecklist')}
                </Button>
              </div>
            </Card>

            {/* Warning Signs */}
            <Card className="p-6 bg-red-50 border-l-4 border-red-500 shadow-sm" data-id="element-1022">
              <div className="flex items-center gap-2 mb-4 text-red-700" data-id="element-1023">
                <AlertOctagon className="h-5 w-5" data-id="element-1024" />
                <h3 className="font-bold" data-id="element-1025">{t('protocol.warningSigns')}</h3>
              </div>
              <ul className="space-y-3" data-id="element-1026">
                {warningSigns?.map((sign, index) => <li key={index} className="text-sm text-red-800 flex items-start gap-2" data-id="element-1027">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-1028" />
                    <span className="leading-snug" data-id="element-1029">{sign}</span>
                  </li>)}
              </ul>
            </Card>

            {/* Template Generator */}
            <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-id="element-1030">
              <div className="flex items-center gap-2 mb-3 text-slate-700" data-id="element-1031">
                <FileText className="h-5 w-5 text-primary-600" data-id="element-1032" />
                <h3 className="font-bold" data-id="element-1033">{t('protocol.needTemplate')}</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4" data-id="element-1034">
                {t('protocol.templateDesc')}
              </p>
              <Button href={`/${language}/report-template-generator`} variant="outline" size="sm" fullWidth data-id="element-1035">
                {t('protocol.goToGenerator')}
              </Button>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 lg:row-start-1 space-y-8 order-2 lg:order-1" data-id="element-1036">
            {/* Immediate Steps */}
            <section className="animate-fade-in" data-id="element-1037">
              <div className="flex items-center gap-3 mb-6" data-id="element-1038">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white font-bold text-sm shadow-sm" data-id="element-1039">
                  1
                </span>
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-1040">
                  {t('protocol.immediateSteps')}
                </h2>
              </div>

              <Card className="overflow-hidden border-t-4 border-t-primary-600 shadow-md" data-id="element-1041">
                <div className="p-6 sm:p-8" data-id="element-1042">
                  <ul className="space-y-6" data-id="element-1043">
                    {steps?.map((step, index) => <li key={index} className="flex items-start gap-4 group" data-id="element-1044">
                        <div className="flex-shrink-0 mt-1" data-id="element-1045">
                          <div className="w-6 h-6 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center group-hover:bg-primary-100 transition-colors" data-id="element-1046">
                            <CheckSquare className="h-4 w-4" data-id="element-1047" />
                          </div>
                        </div>
                        <span className="text-lg text-slate-700 font-medium leading-relaxed" data-id="element-1048">
                          {step}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </Card>
            </section>

            {/* Evidence Checklist */}
            <section className="animate-fade-in delay-100" data-id="element-1049">
              <div className="flex items-center gap-3 mb-6" data-id="element-1050">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-800 text-white font-bold text-sm shadow-sm" data-id="element-1051">
                  2
                </span>
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-1052">
                  {t('protocol.evidenceToCollect')}
                </h2>
              </div>

              <Card className="shadow-md bg-slate-50 border-slate-200" data-id="element-1053">
                <div className="p-6 sm:p-8" data-id="element-1054">
                  <p className="text-slate-600 mb-6" data-id="element-1055">
                    {t('protocol.evidenceDesc')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1056">
                    {evidenceChecklist?.map((item, index) => <div key={index} className="flex items-center p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-primary-200 transition-colors" data-id="element-1057">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" data-id="element-1058" />
                        <span className="text-slate-700 font-medium" data-id="element-1059">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>
              </Card>
            </section>

            {/* Final CTA */}
            <div className="pt-8 animate-fade-in delay-200" data-id="element-1060">
              <div className="bg-primary-900 rounded-2xl p-8 text-center text-white shadow-xl relative overflow-hidden" data-id="element-1061">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-1062"></div>
                <div className="relative z-10" data-id="element-1063">
                  <h3 className="text-2xl font-bold mb-4" data-id="element-1064">
                    {t('protocol.readyToReport')}
                  </h3>
                  <p className="text-primary-100 mb-8 max-w-lg mx-auto" data-id="element-1065">
                    {t('protocol.readyToReportDesc')}
                  </p>
                  <Button href={`/${language}/where-to-report`} size="xl" variant="secondary" className="font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1067" />} data-id="element-1066">
                    {t('protocol.findReportingChannels')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>;
}